/**
 * Event-based API for document version tracking.
 *
 * Uses ``createPanelBus`` for open/close/subscribe coordination.
 * Plain functions callable from non-React contexts (like Markdown
 * component overrides).
 */

import { createPanelBus } from "@/lib/panelBus";

// ─── Version panel events ────────────────────────────────────────────────────

export type VersionPanelRequest = {
  lineageUuid: string;
  sessionUuid: string;
};

const bus = createPanelBus<VersionPanelRequest>();

/** Open the document version panel for a specific lineage. */
export function openDocumentVersionPanel(lineageUuid: string, sessionUuid: string): void {
  bus.open({ lineageUuid, sessionUuid });
}

/** Close the document version panel. Callable from anywhere. */
export const closeDocumentVersionPanel = bus.close;

export const subscribeVersionPanel = bus.subscribe;

// ─── Attachment version registry ─────────────────────────────────────────────
//
// A module-level Map that maps attachment UUIDs to their version/lineage info.
// Populated by:
//   1. The streaming hook (from tool_end SSE events with artifact metadata)
//   2. The message loading path (from MessageAttachment.version/lineageUuid)
//
// Consumed by:
//   The Markdown `a` override to show version badges and history buttons.

export interface AttachmentVersionInfo {
  version: number;
  lineageUuid: string;
  isRevision: boolean;
  /** The session UUID, needed to open the version panel. */
  sessionUuid: string;
}

const attachmentVersionRegistry = new Map<string, AttachmentVersionInfo>();

/** Register version info for an attachment UUID. */
export function registerAttachmentVersion(attachmentUuid: string, info: AttachmentVersionInfo): void {
  attachmentVersionRegistry.set(attachmentUuid, info);
}

/** Look up version info for an attachment UUID. Returns undefined if not tracked. */
export function getAttachmentVersionInfo(attachmentUuid: string): AttachmentVersionInfo | undefined {
  return attachmentVersionRegistry.get(attachmentUuid);
}

/** Clear all entries (e.g. when navigating away from a session). */
export function clearAttachmentVersionRegistry(): void {
  attachmentVersionRegistry.clear();
  attachmentFilenameRegistry.clear();
}

// ─── Attachment filename registry ────────────────────────────────────────────
//
// Maps attachment UUIDs to their real filename. The Markdown link renderer uses
// this to decide whether a `/chat/attachments/{uuid}/file` link is previewable
// (e.g. a PDF), because the LLM often writes human-readable link TEXT such as
// "Open PDF preview" or "Download the document" — which is NOT the filename and
// has no usable extension. Keyed by UUID (stable) rather than the link text.
//
// Populated by the message-load and streaming paths alongside the version
// registry; cleared together in clearAttachmentVersionRegistry.

const attachmentFilenameRegistry = new Map<string, string>();

/** Register the real filename for an attachment UUID. */
export function registerAttachmentFilename(attachmentUuid: string, filename: string): void {
  if (attachmentUuid && filename) {
    attachmentFilenameRegistry.set(attachmentUuid, filename);
  }
}

/** Look up the real filename for an attachment UUID. Returns undefined if unknown. */
export function getAttachmentFilename(attachmentUuid: string): string | undefined {
  return attachmentFilenameRegistry.get(attachmentUuid);
}
